// File: frontend/src/components/admin/AdminBannedIps.tsx
// src/components/admin/AdminBannedIps.tsx

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaBan,
  FaChevronDown,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCheck,
  FaTrash,
  FaUnlock,
  FaExclamationTriangle,
} from "react-icons/fa";

import {
  BannedIpDto,
  BannedIpFilters,
  getBannedIps,
  banIp,
  unbanIp,
  deleteBannedIp,
} from "../../services/adminBannedIpService";
import { Pagination } from "../../utils/Pagination";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import { AdminSuccessBanner, AdminErrorBanner } from "../common/AdminBanner";
import {
  ADMIN_PAGE_SIZE,
  ADMIN_IP_ADDRESS_MAX_LENGTH,
  ADMIN_REASON_MAX_LENGTH,
} from "../../constants";
// Shared "current / max" counter shown under every free-text modal field
import { AdminCharCounter } from "./common";
import { formatAdminDate } from "../../utils/adminUtils";
// Number input with custom, always-visible up/down arrows (native spinner is unreliable)
import { InputClearButton } from "../common/InputClearButton";
import NumberStepper from "./NumberStepper";
import "./AdminBannedIps.css";

interface AdminBannedIpsProps {
  isAdmin: boolean;
}

export const AdminBannedIps: React.FC<AdminBannedIpsProps> = ({ isAdmin }) => {
  const { t } = useTranslation();

  // ── Data state ────────────────────────────────────────────────
  const [bannedIps, setBannedIps] = useState<BannedIpDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage, clearSuccess] = useAutoDismiss();

  // ── Pagination state ──────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // ── Filter state ──────────────────────────────────────────────
  const [filters, setFilters] = useState<BannedIpFilters>({
    search: "",
    active: "",
    sortBy: "bannedAt",
    sortDir: "desc",
  });
  const [searchInput, setSearchInput] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // ── Modal state ───────────────────────────────────────────────
  const [modalType, setModalType] = useState<"ban" | "unban" | "delete" | null>(null);
  const [selectedIp, setSelectedIp] = useState<BannedIpDto | null>(null);

  // ── Ban form state ────────────────────────────────────────────
  const [banForm, setBanForm] = useState({
    ipAddress: "",
    reason: "",
    expirationDays: "" as string | number,
    isPermanent: true,
  });
  const [modalError, setModalError] = useState<string | null>(null);

  // ── Delete confirmation state (enterprise double-confirm) ─────
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // ── Fetch banned IPs ──────────────────────────────────────────
  const fetchBannedIps = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBannedIps(currentPage, ADMIN_PAGE_SIZE, filters);
      setBannedIps(response.content);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    } catch (err: any) {
      setError(err.message || t("admin_ip_fetch_error"));
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, t]);

  useEffect(() => {
    fetchBannedIps();
  }, [fetchBannedIps]);

  // ── Search handler ────────────────────────────────────────────
  const handleSearch = () => {
    setFilters((prev) => ({ ...prev, search: searchInput }));
    setCurrentPage(0);
  };

  // Clear-X next to the search box: empties the field AND drops the applied search
  const handleClearSearch = () => {
    setSearchInput("");
    setFilters((prev) => ({ ...prev, search: "" }));
    setCurrentPage(0);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  // ── Sort handler ──────────────────────────────────────────────
  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortDir: prev.sortBy === field && prev.sortDir === "asc" ? "desc" : "asc",
    }));
    setCurrentPage(0);
  };

  // ── Render sort icon ──────────────────────────────────────────
  const renderSortIcon = (field: string) => {
    if (filters.sortBy !== field)
      return <FaSort className="sort-icon" />;
    return filters.sortDir === "asc" ? (
      <FaSortUp className="sort-icon active" />
    ) : (
      <FaSortDown className="sort-icon active" />
    );
  };

  // ── Clear filters ─────────────────────────────────────────────
  const clearFilters = () => {
    setFilters({ search: "", active: "", sortBy: "bannedAt", sortDir: "desc" });
    setSearchInput("");
    setCurrentPage(0);
  };

  const hasActiveFilters = filters.search || filters.active;

  // ── Modal openers ─────────────────────────────────────────────
  const openBanModal = () => {
    setBanForm({ ipAddress: "", reason: "", expirationDays: "", isPermanent: true });
    setModalError(null);
    setModalType("ban");
  };

  const openUnbanModal = (ip: BannedIpDto) => {
    setSelectedIp(ip);
    setModalError(null);
    setModalType("unban");
  };

  const openDeleteModal = (ip: BannedIpDto) => {
    setSelectedIp(ip);
    setDeleteConfirmText("");
    setModalError(null);
    setModalType("delete");
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedIp(null);
    setModalError(null);
    setDeleteConfirmText("");
  };

  // ── Translate backend error codes to localized messages ────
  const translateIpError = (msg: string): string => {
    const errorMap: Record<string, string> = {
      IP_INVALID_FORMAT: t("admin_ip_error_invalid_format"),
      IP_LOOPBACK_BANNED: t("admin_ip_error_loopback"),
      IP_ALREADY_BANNED: t("admin_ip_error_already_banned"),
    };
    return errorMap[msg] || msg || t("admin_action_failed");
  };

  // ── Ban IP handler ────────────────────────────────────────────
  const handleBanIp = async () => {
    const ip = banForm.ipAddress.trim();
    if (!ip) {
      setModalError(t("admin_ip_required"));
      return;
    }

    try {
      await banIp({
        ipAddress: ip,
        reason: banForm.reason || undefined,
        expirationDays: banForm.isPermanent
          ? null
          : banForm.expirationDays
          ? Number(banForm.expirationDays)
          : null,
      });
      setSuccessMessage(t("admin_ip_ban_success", { ip }));
      closeModal();
      fetchBannedIps();
    } catch (err: any) {
      setModalError(translateIpError(err.message));
    }
  };

  // ── Unban handler ─────────────────────────────────────────────
  const handleUnbanIp = async () => {
    if (!selectedIp) return;
    try {
      await unbanIp(selectedIp.id);
      setSuccessMessage(t("admin_ip_unban_success", { ip: selectedIp.ipAddress }));
      closeModal();
      fetchBannedIps();
    } catch (err: any) {
      setModalError(translateIpError(err.message));
    }
  };

  // ── Delete handler (enterprise double-confirm) ────────────────
  const handleDeleteIp = async () => {
    if (!selectedIp) return;
    // Enterprise security: require typing the IP address to confirm deletion
    if (deleteConfirmText !== selectedIp.ipAddress) {
      setModalError(t("admin_ip_delete_confirm_mismatch"));
      return;
    }
    try {
      await deleteBannedIp(selectedIp.id);
      setSuccessMessage(t("admin_ip_delete_success", { ip: selectedIp.ipAddress }));
      closeModal();
      fetchBannedIps();
    } catch (err: any) {
      setModalError(translateIpError(err.message));
    }
  };


  // ── Determine ban status (active / expired / inactive) ────────
  const getBanStatus = (ip: BannedIpDto): "active" | "expired" | "inactive" => {
    if (!ip.isActive) return "inactive";
    if (ip.expiresAt && new Date(ip.expiresAt) < new Date()) return "expired";
    return "active";
  };

  // ══════════════════════════════════════════════════════════════
  // ── RENDER ────────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════

  return (
    <div className="admin-banned-ips">
      {/* Success / Error banners */}
      {successMessage && (
        <AdminSuccessBanner message={successMessage} onDismiss={clearSuccess} />
      )}
      {error && <AdminErrorBanner message={error} />}

      {/* ── Toolbar ──────────────────────────────────────────── */}
      <div className="users-toolbar">
        {/* Search box */}
        <div className="search-box">
          <input
            type="text"
            placeholder={t("admin_ip_search_placeholder")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {(searchInput !== "" || !!filters.search) && (
            <InputClearButton
              variant="inline"
              className="search-box-clear"
              onClear={handleClearSearch}
            />
          )}
          <button className="search-btn" onClick={handleSearch}>
            <FaSearch />
          </button>
        </div>

        {/* Filter toggle */}
        <button
          className={`filter-toggle ${showFilters ? "active" : ""}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter />
          <span>{t("admin_filters")}</span>
          <FaChevronDown className={`chevron ${showFilters ? "open" : ""}`} />
        </button>

        {/* Clear filters */}
        {hasActiveFilters && (
          <button className="clear-filters" onClick={clearFilters}>
            <FaTimes /> {t("admin_clear_filters")}
          </button>
        )}

        {/* Ban IP button */}
        <button className="ban-ip-btn" onClick={openBanModal}>
          <FaBan /> {t("admin_ip_ban_new")}
        </button>
      </div>

      {/* ── Filter panel ─────────────────────────────────────── */}
      {showFilters && (
        <div className="admin-filter-panel">
          <div className="admin-filter-group">
            <label>{t("admin_ip_filter_status")}</label>
            <select
              value={filters.active}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, active: e.target.value }));
                setCurrentPage(0);
              }}
            >
              <option value="">{t("admin_all")}</option>
              <option value="true">{t("admin_ip_active")}</option>
              <option value="false">{t("admin_ip_inactive")}</option>
            </select>
          </div>
        </div>
      )}

      {/* ── Table ────────────────────────────────────────────── */}
      {loading ? (
        <div className="banned-ips-loading">{t("loading")}</div>
      ) : bannedIps.length === 0 ? (
        <div className="banned-ips-empty">{t("admin_ip_empty")}</div>
      ) : (
        <>
          <div className="banned-ips-table-container">
            <table className="banned-ips-table">
              <thead>
                <tr>
                  <th className="col-ip-id" onClick={() => handleSort("id")}>
                    ID {renderSortIcon("id")}
                  </th>
                  <th className="col-ip-address" onClick={() => handleSort("ipAddress")}>
                    {t("admin_ip_col_address")} {renderSortIcon("ipAddress")}
                  </th>
                  <th className="col-ip-reason">
                    {t("admin_ip_col_reason")}
                  </th>
                  <th className="col-ip-banned-by">
                    {t("admin_ip_col_banned_by")}
                  </th>
                  <th className="col-ip-date" onClick={() => handleSort("bannedAt")}>
                    {t("admin_ip_col_banned_at")} {renderSortIcon("bannedAt")}
                  </th>
                  <th className="col-ip-expires" onClick={() => handleSort("expiresAt")}>
                    {t("admin_ip_col_expires")} {renderSortIcon("expiresAt")}
                  </th>
                  <th className="col-ip-status" onClick={() => handleSort("isActive")}>
                    {t("admin_ip_col_status")} {renderSortIcon("isActive")}
                  </th>
                  <th className="col-ip-user">
                    {t("admin_ip_col_user")}
                  </th>
                  <th className="col-ip-actions">{t("admin_col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {bannedIps.map((ip) => {
                  const status = getBanStatus(ip);
                  return (
                    <tr key={ip.id} className={status !== "active" ? "row-inactive" : ""}>
                      <td className="col-ip-id">{ip.id}</td>
                      <td className="col-ip-address">
                        <span className="ip-address-cell">{ip.ipAddress}</span>
                      </td>
                      <td className="col-ip-reason">
                        <span className="reason-cell">
                          {ip.reason || "-"}
                        </span>
                      </td>
                      <td className="col-ip-banned-by">
                        {ip.bannedByUsername ? (
                          <span className="banned-by-link">@{ip.bannedByUsername}</span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="col-ip-date">
                        <span className="date-text">{formatAdminDate(ip.bannedAt)}</span>
                      </td>
                      <td className="col-ip-expires">
                        <span className="date-text">
                          {ip.expiresAt ? formatAdminDate(ip.expiresAt) : t("admin_ip_permanent")}
                        </span>
                      </td>
                      <td className="col-ip-status">
                        <span className={`ip-status-badge ${status}`}>
                          {status === "active"
                            ? t("admin_ip_active")
                            : status === "expired"
                            ? t("admin_ip_expired")
                            : t("admin_ip_inactive")}
                        </span>
                      </td>
                      <td className="col-ip-user">
                        {ip.ownerUsername ? (
                          <span className="banned-by-link">@{ip.ownerUsername}</span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="col-ip-actions">
                        <div className="ip-actions">
                          {/* Unban button — ADMIN only */}
                          {isAdmin && ip.isActive && (
                            <button
                              className="action-btn success"
                              title={t("admin_ip_unban")}
                              onClick={() => openUnbanModal(ip)}
                            >
                              <FaUnlock />
                            </button>
                          )}
                          {/* Delete button — ADMIN only */}
                          {isAdmin && (
                            <button
                              className="action-btn danger"
                              title={t("admin_ip_delete")}
                              onClick={() => openDeleteModal(ip)}
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="banned-ips-pagination">
              <Pagination
                currentPage={currentPage + 1}
                totalPages={totalPages}
                totalItems={totalItems}
                paginate={(p: number) => setCurrentPage(p - 1)}
              />
              <span className="page-info">
                {t("admin_total_items", { count: totalItems })}
              </span>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════
       * ── MODALS ────────────────────────────────────────────────
       * ══════════════════════════════════════════════════════════ */}

      {/* ── Ban IP Modal ─────────────────────────────────────── */}
      {modalType === "ban" && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div
            className="admin-modal ban-ip-modal"

          >
            <div className="admin-modal-header">
              <h3>{t("admin_ip_ban_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}

              <div className="form-group">
                <label>{t("admin_ip_col_address")} *</label>
                <input
                  type="text"
                  placeholder="192.168.1.100"
                  value={banForm.ipAddress}
                  maxLength={ADMIN_IP_ADDRESS_MAX_LENGTH}
                  onChange={(e) =>
                    setBanForm((prev) => ({ ...prev, ipAddress: e.target.value }))
                  }
                  autoFocus
                />
                {/* Hint and counter share one line so the field lines up with
                    the counter-only fields below it. */}
                <div className="admin-field-footer">
                  <div className="form-hint">{t("admin_ip_format_hint")}</div>
                  <AdminCharCounter value={banForm.ipAddress} max={ADMIN_IP_ADDRESS_MAX_LENGTH} />
                </div>
              </div>

              <div className="form-group">
                <label>{t("admin_ip_col_reason")}</label>
                <textarea
                  placeholder={t("admin_ip_reason_placeholder")}
                  value={banForm.reason}
                  onChange={(e) =>
                    setBanForm((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  maxLength={ADMIN_REASON_MAX_LENGTH}
                />
                <AdminCharCounter value={banForm.reason} max={ADMIN_REASON_MAX_LENGTH} />
              </div>

              {/* Checkbox row — uses inline-flex to avoid form-group column layout */}
              <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", marginBottom: "var(--spacing-md)" }}>
                <input
                  type="checkbox"
                  id="banFormPermanent"
                  checked={banForm.isPermanent}
                  onChange={(e) =>
                    setBanForm((prev) => ({
                      ...prev,
                      isPermanent: e.target.checked,
                      expirationDays: e.target.checked ? "" : prev.expirationDays,
                    }))
                  }
                />
                <label htmlFor="banFormPermanent" style={{ margin: 0, fontWeight: "var(--font-weight-medium)", color: "var(--text-secondary)", fontSize: "var(--font-size-sm)", cursor: "pointer" }}>
                  {t("admin_ip_permanent")}
                </label>
              </div>

              {!banForm.isPermanent && (
                <div className="form-group">
                  <label>{t("admin_ip_expiration_days")}</label>
                  <NumberStepper
                    value={banForm.expirationDays === "" ? null : Number(banForm.expirationDays)}
                    min={1}
                    max={3650}
                    placeholder="30"
                    emptyValue={null}
                    onChange={(next) =>
                      setBanForm((prev) => ({
                        ...prev,
                        // Keep the existing string-backed state: empty stays "", otherwise the number
                        expirationDays: next ?? "",
                      }))
                    }
                  />
                </div>
              )}
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>
                {t("admin_cancel")}
              </button>
              <button className="modal-delete-btn" onClick={handleBanIp}>
                <FaBan /> {t("admin_ip_ban_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Unban Modal ──────────────────────────────────────── */}
      {modalType === "unban" && selectedIp && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div
            className="admin-modal"

          >
            <div className="admin-modal-header">
              <h3>{t("admin_ip_unban_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}
              <p>
                {t("admin_ip_unban_confirm_msg", { ip: selectedIp.ipAddress })}
              </p>
              <div className="delete-ip-address">{selectedIp.ipAddress}</div>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>
                {t("admin_cancel")}
              </button>
              <button className="modal-confirm-btn" onClick={handleUnbanIp}>
                <FaCheck /> {t("admin_ip_unban")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Modal (enterprise double-confirm) ─────────── */}
      {modalType === "delete" && selectedIp && (
        <div className="admin-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div
            className="admin-modal"

          >
            <div className="admin-modal-header">
              <h3>{t("admin_ip_delete_title")}</h3>
              <button className="modal-close-btn" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal-body">
              {modalError && <div className="modal-error">{modalError}</div>}

              <div className="delete-ip-warning">
                <FaExclamationTriangle size={36} />
                <p>{t("admin_ip_delete_warning")}</p>
                <div className="delete-ip-address">{selectedIp.ipAddress}</div>
              </div>

              {/* Enterprise security: user must type the IP to confirm deletion */}
              <div className="form-group">
                <label>{t("admin_ip_delete_type_to_confirm", { ip: selectedIp.ipAddress })}</label>
                <input
                  type="text"
                  placeholder={selectedIp.ipAddress}
                  value={deleteConfirmText}
                  maxLength={ADMIN_IP_ADDRESS_MAX_LENGTH}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  autoFocus
                />
                <AdminCharCounter value={deleteConfirmText} max={ADMIN_IP_ADDRESS_MAX_LENGTH} />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="modal-cancel-btn" onClick={closeModal}>
                {t("admin_cancel")}
              </button>
              <button
                className="modal-delete-btn"
                onClick={handleDeleteIp}
                disabled={deleteConfirmText !== selectedIp.ipAddress}
              >
                <FaTrash /> {t("admin_ip_delete_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};